import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { TESTIDS } from "@/constants/testIds";
import { Search, ArrowRight } from "lucide-react";
import { useWorkspaceTranslation } from "@/locales/workspaceExtras";

const CATS = [
  { key: "all", tKey: "tpl_cat_all", testid: TESTIDS.tplCategoryAll },
  { key: "commercial", tKey: "tpl_cat_commercial", testid: TESTIDS.tplCategoryCommercial },
  { key: "documentary", tKey: "tpl_cat_documentary", testid: TESTIDS.tplCategoryDocumentary },
  { key: "shipping", tKey: "tpl_cat_shipping", testid: TESTIDS.tplCategoryShipping },
  { key: "management", tKey: "tpl_cat_management", testid: TESTIDS.tplCategoryManagement },
];

export default function TemplatesPage() {
  const { t } = useI18n();
  const { tr } = useWorkspaceTranslation();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");

  useEffect(() => { api.listTemplates().then(setItems); }, []);

  const filtered = useMemo(() => {
    return items.filter((tpl) => {
      if (cat !== "all" && tpl.category !== cat) return false;
      if (!q) return true;
      const s = q.toLowerCase();
      return tpl.code.toLowerCase().includes(s) || tr(tpl.title).toLowerCase().includes(s) || tr(tpl.description).toLowerCase().includes(s);
    });
  }, [items, q, cat, tr]);

  return (
    <div className="px-4 py-6 sm:p-6 md:p-10 xl:p-12">
      <div className="mb-10">
        <div className="kicker mb-3">— {t("d_nav_templates")}</div>
        <h1 className="font-serif text-4xl mb-2">{t("tpl_title")}</h1>
        <p className="text-[var(--td-text-2)] max-w-2xl">{t("tpl_sub")}</p>
      </div>

      <div className="flex flex-col md:flex-row gap-4 mb-8">
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute top-1/2 -translate-y-1/2 left-3 text-[var(--td-text-3)]" />
          <input
            data-testid={TESTIDS.tplSearch}
            placeholder={t("tpl_search")}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="td-input pl-9"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          {CATS.map((c) => (
            <button
              key={c.key}
              data-testid={c.testid}
              onClick={() => setCat(c.key)}
              className={`font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 border transition-colors ${
                cat === c.key
                  ? "border-[var(--td-gold)] text-[var(--td-gold)]"
                  : "border-[var(--td-border)] text-[var(--td-text-2)] hover:border-[var(--td-text-2)]"
              }`}
            >
              {t(c.tKey)}
            </button>
          ))}
        </div>
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {filtered.map((tpl) => (
          <div key={tpl.id} data-testid={TESTIDS.tplCard} className="card-surface p-6 flex flex-col hover:border-[var(--td-gold)] transition-colors">
            <div className="flex items-center justify-between mb-3">
              <span className="font-mono text-xs text-[var(--td-gold)]">{tpl.code}</span>
              <span className="font-mono text-[10px] uppercase tracking-widest text-[var(--td-text-3)]">{t(CATS.find((item) => item.key === tpl.category)?.tKey || "tpl_cat_all")}</span>
            </div>
            <div className="font-serif text-2xl mb-2">{tr(tpl.title)}</div>
            <p className="text-[var(--td-text-2)] text-sm leading-relaxed flex-1 mb-5">{tr(tpl.description)}</p>
            <button
              data-testid={TESTIDS.tplUse}
              onClick={() => nav(`/app/drafting?code=${tpl.code}`)}
              className="btn-ghost text-xs inline-flex items-center justify-center gap-2 hover:!border-[var(--td-gold)]"
            >
              {t("tpl_use")} <ArrowRight size={13} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
